import { throttle } from "lodash";
import { useDialog } from "primevue/usedialog";
import type { Ref } from "vue";
import { nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router";

import ArtefactDraft from "@/components/ArtefactDraft.vue";
import { useEditManager, useEditStore } from "@/composables/edit-manager";
import {
    ArtefactOPFSFile,
    type ArtefactOPFSIdentity,
} from "@/lib/ArtefactOPFSFile";
import type { Artefact } from "@/lib/artefacts";
import {
    type EntityArgTypes,
    type SyncReturn,
    type SyncableEntityType,
    pagePerEntityAttribute,
    useSyncStore,
} from "@/stores/sync-store";

import { useOrganizationManager } from "./organization-manager";
import { registerFlush } from "./pending-flush";

export const FILE_SYNC_THROTTLE_MS = 500;

interface DraftOpts<T extends SyncableEntityType> {
    entityType: ArtefactOPFSIdentity<T>["entityType"];
    entityId: Ref<string | undefined>;
    attribute: ArtefactOPFSIdentity<T>["attribute"];
    artefact: Ref<Artefact | undefined>;
}

export const useDraftManager = <T extends SyncableEntityType>(
    opts: DraftOpts<T>
) => {
    const { artefact } = opts;
    const route = useRoute();

    const dialog = useDialog();
    const { resetFailedSyncs } = useSyncStore();

    const draft = ref<ArtefactOPFSFile<T>>();
    const { isEditing } = useEditManager();
    const { organization } = useOrganizationManager();
    const { startEditing } = useEditStore();

    const diffFrom = ref(new Blob([""]));
    const diffTo = ref(new Blob([""]));

    const restoreDraft = async () => {
        if (draft.value && draft.value.handle) {
            const data = await (await draft.value.handle.getFile()).text();
            artefact.value?.updateContent(data);
        }
        startEditing();

        if (!opts.entityId.value || !organization.value || !artefact.value) {
            return;
        }

        // When restoring, the artefact will not actually be deleted from the
        // filesystem as the actual save hasn't actually triggered yet, causing
        // the failed sync to persist. To hide it from the list, we pass the
        // identity of the artefact so that it can be temporarily excluded.
        //
        // It will only re-appear when:
        //  1. The user refreshes the page before the changes were saved to the backend.
        //  2. A subsequent save to the backend has failed.
        //
        //  So hiding it is not actually dangerous. It is just to avoid confusion
        //  for the user.
        await resetFailedSyncs({
            organizationId: organization.value!.identity,
            entityType: opts.entityType as SyncableEntityType,
            entityId: opts.entityId.value,
            attribute:
                opts.attribute as keyof EntityArgTypes[SyncableEntityType],
            mimetype: artefact.value.mimetype,
        });
    };

    const discardDraft = async () => {
        throttledSaveDraft.cancel();
        if (draft.value) {
            await draft.value.remove();
        } else if (
            opts.entityId.value &&
            organization.value &&
            artefact.value
        ) {
            (
                await ArtefactOPFSFile.get({
                    organizationId: organization.value.identity,
                    entityType: opts.entityType,
                    entityId: opts.entityId.value,
                    attribute: opts.attribute,
                    mimetype: artefact.value.mimetype,
                })
            )?.remove();
        }

        // Set to 'undefined', so the draft can be recreated if needed.
        draft.value = undefined;

        await resetFailedSyncs();
    };

    const throttledSaveDraft = throttle(async () => {
        if (!artefact.value) {
            return;
        }

        if (artefact.value?.blob) {
            if (!draft.value && opts.entityId.value && organization.value) {
                // If the draft is not found, it means it is already discarded
                draft.value = await ArtefactOPFSFile.getOrCreate({
                    organizationId: organization.value.identity,
                    entityType: opts.entityType,
                    entityId: opts.entityId.value,
                    attribute: opts.attribute,
                    mimetype: artefact.value.mimetype,
                });
            }
            if (draft.value) {
                await draft.value.write(artefact.value.blob);
            }
        }
    }, FILE_SYNC_THROTTLE_MS);

    // Register with the centralized flush registry so that the router.beforeEach
    // guard can flush this draft write before any navigation.
    registerFlush(() => throttledSaveDraft.flush());

    /**
     * Compare two blobs for equality.
     */
    const blobsEqual = async (blobA: Blob, blobB: Blob): Promise<boolean> => {
        if (blobA.size !== blobB.size) {
            return false;
        }

        const bufferA = await blobA.arrayBuffer();
        const bufferB = await blobB.arrayBuffer();

        async function hash(buffer: ArrayBuffer) {
            const digest = await crypto.subtle.digest("SHA-1", buffer);
            return new Uint8Array(digest).toString();
        }

        return (await hash(bufferA)) === (await hash(bufferB));
    };

    const checkAndRestoreDraft = async () => {
        if (!opts.entityId.value) {
            return;
        }

        if (!artefact.value?.blob) {
            return;
        }

        if (!organization.value) {
            return;
        }

        // No need to show the restore dialog when we're not on the page in which
        // the specific attribute is normally edited.
        if (
            pagePerEntityAttribute[opts.entityType][opts.attribute] !==
            route.name
        ) {
            return;
        }

        draft.value = await ArtefactOPFSFile.get({
            organizationId: organization.value.identity,
            entityType: opts.entityType,
            entityId: opts.entityId.value,
            attribute: opts.attribute,
            mimetype: artefact.value.mimetype,
        });

        if (!draft.value || !draft.value.handle) {
            return;
        }

        const data = await draft.value.handle.getFile();

        // Stale/empty draft — clean it up rather than showing a restore dialog
        // with no content. This can happen when the OPFS file was created but
        // the pending write was abandoned (e.g. the throttle fired after
        // navigation) before any content was flushed into it.
        if (data.size === 0) {
            await draft.value.remove();
            draft.value = undefined;
            return;
        }

        // Artefact does not exist, or is exactly the same as stored
        if (!data || (await blobsEqual(data, artefact.value.blob))) {
            draft.value.remove();
            await resetFailedSyncs();
            return;
        }

        diffFrom.value = artefact.value.blob;
        diffTo.value = data;

        dialog.open(ArtefactDraft, {
            props: {
                header: `Draft found for ${artefact.value.name}. Restore it?`,
                closable: false,
                modal: true,
                draggable: false,
                style: {
                    width: "80vw",
                },
            },
            data: {
                artefact: artefact.value,
                diffFrom: diffFrom.value,
                diffTo: diffTo.value,
            },
            emits: {
                onRestore: () => {
                    restoreDraft();
                },
                onDiscard: () => {
                    discardDraft();
                },
            },
        });
    };

    watch(
        () => artefact.value?.blob,
        () => {
            if (!artefact.value) {
                return;
            }

            if (!artefact.value.isModified) {
                return;
            }

            if (isEditing.value) {
                throttledSaveDraft();
            }
        }
    );

    watch(
        () => isEditing.value,
        async (newValue) => {
            // If we just started editing and the file is modified already (e.g.
            // because the user overwrites a file by uploading a new file) - save
            // the draft.
            if (newValue === true && artefact.value?.isModified) {
                await throttledSaveDraft();
            }
        }
    );

    // Also trigger on 'route' changes, as when you switch from the definition page
    // to the process page you'll keep the same identities around.
    watch(
        [() => route.name, () => artefact.value?.identity],
        () => {
            if (artefact.value?.identity) {
                nextTick(async () => {
                    await checkAndRestoreDraft();
                });
            }
        },
        { immediate: true }
    );

    return {
        discardDraft,
    };
};

export function discardDraftsAfterSync(
    entityId: Ref<string | undefined>,
    drafts: ReturnType<typeof useDraftManager>[],
    entityType: SyncableEntityType
) {
    const syncStore = useSyncStore();
    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after(async (fullySyncedIdentities: SyncReturn) => {
                // When the entity is fully synced, discard the drafts.
                //
                // Note that when we switched to a different page while an artefact is still being synced, the draft
                // will effectively still be discarded, just not through the draft manager but through the sync store
                // directly. This will happen through the discardPromises.
                // If still editing, the draft doesn't have to be removed either.
                if (!entityId.value) {
                    return;
                }

                for (const identity of fullySyncedIdentities) {
                    if (
                        identity.entityType === entityType &&
                        identity.entityId === entityId.value
                    ) {
                        for (const draft of drafts) {
                            try {
                                await draft.discardDraft();
                            } catch (e) {
                                if (!(e instanceof DOMException)) {
                                    // DOMExecption occurs if draft was already removed.
                                    throw e;
                                }
                            }
                        }
                        break;
                    }
                }
            });
        }
    });
}
